// Utility function to debounce function calls
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// List of common email domains
const COMMON_DOMAINS = [
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'icloud.com',
    'bellsouth.net',
    'ymail.com',
    'aol.com',
    'mail.com',
    'protonmail.com',
    'live.co.uk',
    'hotmail.co.uk',
    'hotmail.fr',
    'hotmail.de',
    'hotmail.it',
    'hotmail.es',
    'hotmail.com.au',
    'hotmail.ca',
    'mac.com',
    'me.com',
];

// Improved Levenshtein Distance Function
function levenshteinDistance(a, b) {
    const alen = a.length;
    const blen = b.length;

    if (alen === 0) return blen;
    if (blen === 0) return alen;

    const matrix = Array.from({ length: blen + 1 }, () => new Array(alen + 1));

    for (let i = 0; i <= blen; i++) {
        matrix[i][0] = i;
    }
    for (let j = 0; j <= alen; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= blen; i++) {
        for (let j = 1; j <= alen; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1, // insertion
                    matrix[i - 1][j] + 1, // deletion
                );
            }
        }
    }

    return matrix[blen][alen];
}

// Function to suggest the closest domain
function suggestDomain(domain) {
    let bestMatch = null;
    let smallestDistance = Infinity;
    for (let commonDomain of COMMON_DOMAINS) {
        const distance = levenshteinDistance(domain, commonDomain);
        if (distance < smallestDistance) {
            smallestDistance = distance;
            bestMatch = commonDomain;
        }
    }
    const maxAllowedDistance = Math.max(2, Math.floor(domain.length * 0.3));
    return smallestDistance <= maxAllowedDistance && bestMatch !== domain ? bestMatch : null;
}

// Email Validation Function
function validateEmail(email) {
    if (email.trim() === '') {
        return { isValid: true };
    }

    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) {
        return { isValid: false, message: 'Please enter a valid email address.' };
    }
    const [localPart, domain] = email.split('@');
    const suggestedDomain = suggestDomain(domain.toLowerCase());
    if (suggestedDomain) {
        return {
            isValid: false,
            message: `Did you mean ${suggestedDomain}?`,
            suggestedDomain: suggestedDomain,
            originalEmail: email,
        };
    }
    return { isValid: true };
}

// Function to show error message with suggestion
function showErrorMessage(field, message, suggestedEmail) {
    let errorSpan = field.parentNode.querySelector('.form-error-message');
    if (!errorSpan) {
        errorSpan = document.createElement('span');
        errorSpan.className = 'form-error-message';
        errorSpan.style.color = 'red';
        errorSpan.style.display = 'block';
        errorSpan.style.marginTop = '5px';
        errorSpan.setAttribute('role', 'alert');
        field.parentNode.appendChild(errorSpan);
    }

    errorSpan.innerHTML = '';

    if (suggestedEmail) {
        const button = document.createElement('button');
        button.textContent = suggestedEmail.split('@')[1];
        button.classList.add('suggested-email');
        button.type = 'button';
        button.setAttribute('aria-label', 'Use suggested email');

        errorSpan.appendChild(document.createTextNode('Did you mean '));
        errorSpan.appendChild(button);
        errorSpan.appendChild(document.createTextNode('?'));

        button.onclick = function (event) {
            event.preventDefault();
            field.value = suggestedEmail;
            clearErrorMessage(field);
        };
    } else {
        errorSpan.textContent = message;
    }

    setTimeout(() => {
        errorSpan.classList.add('show-new-error-message');
    }, 10);
}

// Function to clear the error message
function clearErrorMessage(field) {
    const errorSpan = field.parentNode.querySelector('.form-error-message');
    if (errorSpan) {
        errorSpan.classList.remove('show-new-error-message');
        setTimeout(() => {
            if (errorSpan.parentNode) {
                errorSpan.parentNode.removeChild(errorSpan);
            }
        }, 300);
    }
}

// Debounced Email Validation Handler
const debouncedValidateEmail = debounce(function() {
    // Ensure 'this' is the email field
    const emailField = this instanceof HTMLElement ? this : document.querySelector('main input[name="email"]');
    if (!emailField) {
        console.warn('Email field not found');
        return;
    }

    const email = emailField.value || '';

    if (email.trim() === '') {
        clearErrorMessage(emailField);
        return;
    }

    const emailValidation = validateEmail(email);

    if (!emailValidation.isValid && emailValidation.suggestedDomain) {
        const suggestedEmail = email.split('@')[0] + '@' + emailValidation.suggestedDomain;
        showErrorMessage(emailField, emailValidation.message, suggestedEmail);
    } else if (!emailValidation.isValid) {
        showErrorMessage(emailField, emailValidation.message, null);
    } else {
        clearErrorMessage(emailField);
    }
}, 1000);

// Function to handle state transitions
function switchToInitialState() {
    $('.signup-success').removeClass('form-step-active');
    $('.signup-initial').addClass('form-step-active');
}

function switchToSuccessState() {
    // Set cookie to indicate user has signed up
    document.cookie = "user_signed_up_5=true; path=/; max-age=31536000"; // 1 year expiry
    document.cookie = "preShopUser=true; path=/; max-age=31536000"; // 1 year expiry
    
    // Update localStorage too to ensure consistent state across tabs
    localStorage.setItem('preShopUser', 'true');
    
    // Notify other components about the change in pre-shop state
    document.dispatchEvent(new CustomEvent('pre-shop:updated', {
        detail: { isPreShop: true }
    }));
    
    // Unfocus all inputs to dismiss mobile keyboard
    const formInputs = document.querySelectorAll(
        'input[type="text"], input[type="email"], input[type="tel"]',
    );

    // Dispatch signup:success event for the popup to handle
    document.dispatchEvent(new CustomEvent('signup:success'));
    
    if ($(window).width() > 990) {
        // Smooth scroll using jQuery animate
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        $('.signup-initial').removeClass('form-step-active');
        setTimeout(function () {
            $('.signup-success').addClass('form-step-active');
        }, 300);
    } else {
        // Smooth scroll using jQuery animate
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        $('.signup-initial').removeClass('form-step-active');
        $('.signup-success').addClass('form-step-active');
    }


    const successCopy = document.querySelectorAll('.success-message-copy');

    if (successCopy && successCopy.length > 0) {
        const navbarMenuColor = successCopy[0].dataset.navbarMenuColor;
        const navbarLogoColor = successCopy[0].dataset.navbarLogoColor;
        const navbarIconsColor = successCopy[0].dataset.navbarIconsColor;

        // Set initial colors
        document.documentElement.style.setProperty('--navbar-menu-color', navbarMenuColor);
        document.documentElement.style.setProperty('--navbar-logo-color', navbarLogoColor);
        document.documentElement.style.setProperty('--navbar-icons-color', navbarIconsColor);

        // Handle brand blocks on desktop
        if (window.innerWidth > 990) {
            const brandBlockHolder = document.querySelector('.brand-block-holder ul');
            const brandBlocks = document.querySelectorAll('.card-brand-block');

            if (brandBlockHolder && brandBlocks.length > 0) {
                // First, make original blocks non-interactive
                brandBlocks.forEach((block) => {
                    // Make original blocks non-interactive
                    block.setAttribute('aria-hidden', 'true');
                    // Make all focusable elements non-focusable
                    block
                        .querySelectorAll('a, button, input, select, textarea, [tabindex]')
                        .forEach((element) => {
                            element.setAttribute('tabindex', '-1');
                        });
                });

                // Clone and setup accessible blocks
                brandBlocks.forEach((block, index) => {
                    const clonedBlock = block.cloneNode(true);

                    // Reset aria-hidden since this will be visible
                    clonedBlock.removeAttribute('aria-hidden');

                    // Make all interactive elements focusable again
                    clonedBlock
                        .querySelectorAll('a, button, input, select, textarea, [tabindex="-1"]')
                        .forEach((element) => {
                            element.removeAttribute('tabindex');
                        });

                    // Add to carousel
                    brandBlockHolder.appendChild(clonedBlock);
                });

                // Initialize brand block scroll handler
                const handleBrandBlockScroll = () => {
                    const products = document.querySelectorAll('main .plp-product-standard');
                    const brandBlocksInHolder =
                        brandBlockHolder.querySelectorAll('.card-brand-block');

                    if (!products.length || !brandBlocksInHolder.length) return;

                    const initialProductCount = 6; // First brand block shows after 6 products
                    const productsPerBlock = 8; // Subsequent blocks show every 8 products
                    const windowHeight = window.innerHeight;
                    let activeBlockIndex = -1;

                    // Find which group of products is most visible
                    products.forEach((product, index) => {
                        const rect = product.getBoundingClientRect();
                        // Only consider products that are at least halfway into view
                        const isVisible = rect.top < windowHeight - rect.height / 2;

                        if (isVisible) {
                            if (index < initialProductCount) {
                                // Don't activate any block yet
                                return;
                            }

                            // Calculate which block should be active
                            const productsScrolled = index - initialProductCount;

                            if (productsScrolled >= 0) {
                                activeBlockIndex = Math.min(
                                    Math.floor(productsScrolled / productsPerBlock),
                                    brandBlocksInHolder.length - 1,
                                );
                            }
                        }
                    });

                    // Update brand block visibility and accessibility
                    brandBlocksInHolder.forEach((block, index) => {
                        const isActive = index === activeBlockIndex;
                        block.classList.toggle('brand-block-active', isActive);

                        // Update accessibility attributes based on visibility
                        block.setAttribute('aria-hidden', (!isActive).toString());

                        // Update tabindex of interactive elements based on visibility
                        block
                            .querySelectorAll('a, button, input, select, textarea')
                            .forEach((element) => {
                                element.setAttribute('tabindex', isActive ? '0' : '-1');
                            });
                    });
                };

                // Debounce the scroll handler
                const debouncedBrandBlockScroll = debounce(handleBrandBlockScroll, 10);

                // Add scroll listener
                window.addEventListener('scroll', debouncedBrandBlockScroll);

                // Initial check for brand blocks
                setTimeout(handleBrandBlockScroll, 500);

                // Also check when products are dynamically loaded
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.addedNodes.length) {
                            handleBrandBlockScroll();
                        }
                    });
                });

                // Start observing the product container for changes
                const productContainer = document.querySelector('main');
                if (productContainer) {
                    observer.observe(productContainer, { childList: true, subtree: true });
                }
            }
        }

        // Add scroll handler for success message
        const handleSuccessScroll = () => {
            const signupSuccess = document.querySelector('.signup-success');
            if (!signupSuccess) return;

            const parentEditorial = signupSuccess.closest('.block-editorial');
            if (!parentEditorial) return;

            const rect = parentEditorial.getBoundingClientRect();
            const topBarOffset = 35;

            if (rect.top <= topBarOffset) {
                // When scrolled past threshold, set to black
                document.documentElement.style.setProperty('--navbar-menu-color', '#000');
                document.documentElement.style.setProperty('--navbar-logo-color', '#000');
                document.documentElement.style.setProperty('--navbar-icons-color', '#000');
            } else {
                // When above threshold, use success message colors
                document.documentElement.style.setProperty(
                    '--navbar-menu-color',
                    navbarMenuColor,
                );
                document.documentElement.style.setProperty(
                    '--navbar-logo-color',
                    navbarLogoColor,
                );
                document.documentElement.style.setProperty(
                    '--navbar-icons-color',
                    navbarIconsColor,
                );
            }
        };

        // Debounce the scroll handler
        const debouncedSuccessScroll = debounce(handleSuccessScroll, 10);

        // Add scroll listener
        window.addEventListener('scroll', debouncedSuccessScroll);
    }

    setTimeout(function () {
        formInputs.forEach((input) => {
            input.blur();
        });
    }, 200);

    setTimeout(function () {
        $('.signup-success').addClass('show-collection');
    }, 700);

}

// Function to load the dynamic collection content
function loadDynamicCollection() {
    const dynamicCollectionElements = document.querySelectorAll(
        '.dynamic-loaded-collection:not(.is-loading)',
    );

    if (!dynamicCollectionElements.length) return;

    dynamicCollectionElements.forEach((element) => {
        const collectionHandle = element.dataset.collectionHandle;
        if (!collectionHandle) return;

        // Mark as loading to prevent duplicate requests
        element.classList.add('is-loading');

        fetch(`/collections/${collectionHandle}?view=ajax-dynamic-container`)
            .then((response) => response.text())
            .then((html) => {
                element.innerHTML = html;
                element.classList.add('is-loaded');
            })
            .catch((error) => {
                console.error('Error loading collection:', error);
                element.classList.remove('is-loading');
            });
    });
}

// Main function to set up event listeners
document.addEventListener('DOMContentLoaded', function () {
    // Initialize phone input if it exists
    let iti;
    const phoneInput = document.querySelector("#newsletterPhoneNumber");
    if (phoneInput) {
        // Error handling setup for phone validation
        const errorMsg = document.querySelector("#error-msg");
        const validMsg = document.querySelector("#valid-msg");
        
        // Error map for phone validation errors
        const errorMap = [
            "Invalid number",
            "Invalid country code", 
            "Please enter a valid number",
            "Please enter a valid number",
            "Invalid number"
        ];

        // Reset error state
        const resetPhoneValidation = function() {
            phoneInput.classList.remove("error");
            phoneInput.classList.remove("show-error");
            if (errorMsg) {
                errorMsg.innerHTML = "";
                errorMsg.classList.add("hidden-error");
            }
            if (validMsg) {
                validMsg.classList.add("hidden-error");
            }
        };

        // Initialize ITI plugin
        iti = window.intlTelInput(phoneInput, {
            autoPlaceholder: "on",
            formatOnDisplay: true,
            preferredCountries: ['us','ca','gb'],
            separateDialCode: true,
            utilsScript: "/cdn/shop/t/920/assets/utils.js"
        });

        // Set up phone number validation and syncing
        const hiddenPhoneInput = $("#newsletterHiddenPhoneNumber");
        
        // Validate on blur
        phoneInput.addEventListener('blur', function() {
            resetPhoneValidation();
            if (phoneInput.value.trim()) {
                if (iti.isValidNumber()) {
                    if (validMsg) {
                        validMsg.classList.remove("hidden-error");
                    }
                } else {
                    phoneInput.classList.add("error");
                    phoneInput.classList.add("show-error");
                    if (errorMsg) {
                        const errorCode = iti.getValidationError();
                        errorMsg.innerHTML = errorMap[errorCode];
                        errorMsg.classList.remove("hidden-error");
                    }
                }
            }
        });

        // Reset validation on input change
        ["change", "keyup"].forEach(eventType => {
            phoneInput.addEventListener(eventType, resetPhoneValidation);
        });

        // Update hidden input when phone number changes
        ["keyup", "change"].forEach(eventType => {
            phoneInput.addEventListener(eventType, () => {
                hiddenPhoneInput.val(iti.getNumber());
            });
        });

        // Watch for country changes
        const flagContainer = document.querySelector(".iti__flag");
        if (flagContainer) {
            new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.attributeName === "class") {
                        hiddenPhoneInput.val(iti.getNumber());
                    }
                });
            }).observe(flagContainer, { attributes: true });
        }
    }

    // Check if we're in debug mode
    const isDebugMode = new URLSearchParams(window.location.search).get('debug_form') === 'true';

    // Add keyboard shortcut listener if in debug mode
    if (isDebugMode) {
        document.addEventListener('keydown', function (event) {
            // Check for Command (Mac) or Control (Windows) + Option/Alt + 1 or 2
            if ((event.metaKey || event.ctrlKey) && event.altKey) {
                if (event.key === '1') {
                    switchToInitialState();
                } else if (event.key === '2') {
                    switchToSuccessState();
                }
            }
        });
    }

    // Check if we have any dynamic collections to load
    const hasDynamicCollections = document.querySelector('.dynamic-loaded-collection') !== null;
    let hasLoadedCollections = false;

    if (hasDynamicCollections) {
        const form = document.getElementById('sale-page--form');

        // Function to check if element is in viewport
        function isInViewport(element) {
            if (!element) return false;
            const rect = element.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.top <= (window.innerHeight || document.documentElement.clientHeight)
            );
        }

        // Debounced scroll handler with viewport check
        const debouncedScrollHandler = debounce(function () {
            if (!hasLoadedCollections && window.innerWidth <= 990 && isInViewport(form)) {
                loadDynamicCollection();
                hasLoadedCollections = true;
                // Remove scroll listener after loading
                window.removeEventListener('scroll', debouncedScrollHandler);
            }
        }, 150);

        if (form) {
            // Add hover detection for desktop
            if (window.innerWidth > 990) {
                form.addEventListener('mouseenter', () => {
                    if (!hasLoadedCollections) {
                        loadDynamicCollection();
                        hasLoadedCollections = true;
                    }
                });
            }

            // Add scroll detection for mobile
            if (window.innerWidth <= 990) {
                window.addEventListener('scroll', debouncedScrollHandler);
                // Initial check in case form is already in viewport
                debouncedScrollHandler();
            }

            // Keep existing focus detection
            const formInputs = document.querySelectorAll(
                'input[type="text"], input[type="email"], input[type="tel"]',
            );
            formInputs.forEach((input) => {
                input.addEventListener('focus', () => {
                    if (!hasLoadedCollections) {
                        loadDynamicCollection();
                        hasLoadedCollections = true;
                    }
                });
            });
        }
    }

    const emailInput = document.querySelector('input[name="email"]');
    if (emailInput) {
        emailInput.addEventListener('input', function() {
            // Explicitly bind this to the input element
            debouncedValidateEmail.call(emailInput);
        });
    }

    // Helper function to submit data to Attentive
    function submitToAttentive(email, phone, itiInstance, sourceId) {
        if (!phone || !itiInstance) {
            //console.warn('Attentive submission requires phone number and iti instance.');
            return; // Or return a rejected promise if async
        }

        const $phone_number = itiInstance.getNumber();
        const $email = email ? email.toLowerCase() : ''; // Use provided email or empty string
        let attentiveSourceID = sourceId;

        // Double check source ID logic if needed
        if (!attentiveSourceID) {
            if ($('input[name="attentive_id"]').length > 0) {
                attentiveSourceID = $('input[name="attentive_id"]').val();
            } else if (window.signupPageData && window.signupPageData.attentiveID) {
                attentiveSourceID = window.signupPageData.attentiveID;
            } else {
                 //console.warn('Attentive Source ID not found.');
                 // Decide how to handle missing source ID - maybe return or use a default
                 // For now, let's proceed but log a warning
            }
        }

        //console.log(`[submitToAttentive] Phone number from iti.getNumber(): ${$phone_number}`); // Log the number

        // Add a check for empty/invalid number right before sending
        if (!$phone_number || $phone_number === '+') { // Check for empty or just the dial code if iti fails
             //console.error('[submitToAttentive] Error: iti.getNumber() returned an invalid/empty value:', $phone_number);
             // Return a rejected promise so the caller's .catch() is triggered
             return Promise.reject({ 
                 message: 'Invalid phone number format generated.',
                 status: 400 // Simulate a client-side bad request
             }); 
        }

        // Conditionally build the user object
        const userPayload = { phone: $phone_number };
        if ($email) { // Only add email key if it's not empty/null
            userPayload.email = $email;
        }

        const attentiveData = {
            user: userPayload,
            signUpSourceId: attentiveSourceID,
        };

        // Only submit if US flag is selected
        if ($('.iti__flag').hasClass('iti__us')) {
            return $.ajax({ // Return the promise
                type: 'POST',
                url: 'https://krw-attve.herokuapp.com/attve-v2.php',
                data: JSON.stringify(attentiveData),
                dataType: 'json',
            });
        } else {
             //console.log('Non-US number, skipping Attentive submission.');
             return Promise.resolve(); // Resolve immediately if not US
        }
    }

    // Handle form submission
    const form = document.getElementById('sale-page--form');
    if (form) {
        const submitButton = form.querySelector('.form--btn');

        form.addEventListener('submit', function (event) {
            event.preventDefault();

            // Add loading state to button
            submitButton.classList.add('is-loading');

            // --- Field Identification ---
            const emailField = this.querySelector('input[name="email"]');
            const phoneField = document.getElementById('newsletterPhoneNumber'); // Assumes global `iti` is initialized if phoneField exists
            const hasEmailField = !!emailField;
            const hasPhoneField = !!phoneField;

            let emailValue = '';
            let phoneValue = '';
            let isPhoneValid = false;
            let isEmailValid = false;

            // Check if we're in debug mode
            const isDebugMode =
                new URLSearchParams(window.location.search).get('debug_form') === 'true';

            // If in debug mode, simulate loading for a moment before showing success
            if (isDebugMode) {
                // Simulate network delay in debug mode
                setTimeout(() => {
                    submitButton.classList.remove('is-loading');
                    switchToSuccessState();
                }, 1000);
                return;
            }

            // --- Validation ---
            let hasErrors = false;

            // Phone validation (if field exists)
            if (hasPhoneField) {
                phoneValue = phoneField.value.trim();
                if (!phoneValue) {
                    // Display error for empty phone field
                    const wrapper = phoneField.closest('.form-input-wrapper');
                    let errorSpan = wrapper.querySelector('.form-error-message');
                    if (!errorSpan) {
                        errorSpan = document.createElement('span');
                        errorSpan.className = 'form-error-message';
                        errorSpan.style.color = 'red';
                        errorSpan.style.display = 'block';
                        errorSpan.style.marginTop = '5px';
                        errorSpan.setAttribute('role', 'alert');
                        wrapper.appendChild(errorSpan);
                    }
                    errorSpan.textContent = 'Phone number is required';
                    setTimeout(() => {
                        errorSpan.classList.add('show-new-error-message');
                    }, 10);
                    hasErrors = true;

                    const clearPhoneError = function() {
                        const wrapper = phoneField.closest('.form-input-wrapper');
                        const errorSpan = wrapper.querySelector('.form-error-message');
                        if (errorSpan) {
                            errorSpan.classList.remove('show-new-error-message');
                            setTimeout(() => {
                                if (errorSpan.parentNode) {
                                    errorSpan.parentNode.removeChild(errorSpan);
                                }
                            }, 300);
                        }
                        phoneField.removeEventListener('input', clearPhoneError);
                    };
                    phoneField.addEventListener('input', clearPhoneError);
                } else if (iti && !iti.isValidNumber()) {
                    // Display error for invalid phone number (using iti validation message)
                     const wrapper = phoneField.closest('.form-input-wrapper');
                    let errorSpan = wrapper.querySelector('.form-error-message');
                     if (!errorSpan) {
                        errorSpan = document.createElement('span');
                        errorSpan.className = 'form-error-message';
                        errorSpan.style.color = 'red';
                        errorSpan.style.display = 'block';
                        errorSpan.style.marginTop = '5px';
                        errorSpan.setAttribute('role', 'alert');
                        wrapper.appendChild(errorSpan);
                    }
                    const errorCode = iti.getValidationError();
                     // Use the existing errorMap or fallback
                     const errorMap = ["Invalid number", "Invalid country code", "Too short", "Too long", "Invalid number"];
                    errorSpan.textContent = errorMap[errorCode] || 'Please enter a valid phone number.';
                    setTimeout(() => { errorSpan.classList.add('show-new-error-message'); }, 10);
                    hasErrors = true;

                } else {
                    // Phone is present and valid
                    isPhoneValid = true;
                    // Clear any previous errors
                    const wrapper = phoneField.closest('.form-input-wrapper');
                    const errorSpan = wrapper.querySelector('.form-error-message');
                    if (errorSpan) {
                       clearErrorMessage(phoneField); // Use existing clear function if suitable
                    }
                }
            }

            // Email validation (if field exists)
            if (hasEmailField) {
                emailValue = emailField.value.trim();
                const emailValidation = validateEmail(emailValue);
                if (!emailValue || !emailValidation.isValid) {
                     if (emailValidation.suggestedDomain) {
                        const suggestedEmail = emailValue.split('@')[0] + '@' + emailValidation.suggestedDomain;
                        showErrorMessage(emailField, emailValidation.message, suggestedEmail);
                    } else {
                        showErrorMessage(emailField, emailValidation.message || 'Please enter a valid email address.');
                    }
                    hasErrors = true;
                } else {
                    isEmailValid = true;
                    clearErrorMessage(emailField);
                }
            }

             // --- Stop if Validation Errors ---
            if (hasErrors) {
                submitButton.classList.remove('is-loading');
                return; // Stop form submission
            }


            // --- Submission Logic ---

            // Case 1: Only Phone field is present
            if (hasPhoneField && !hasEmailField && isPhoneValid) {
                 //console.log('Submitting phone only to Attentive...');
                 const sourceId = $('input[name="attentive_id"]').val() || (window.signupPageData ? window.signupPageData.attentiveID : null);
                 submitToAttentive(null, phoneValue, iti, sourceId) // Pass null for email
                    .then(response => {
                        // *** Log the actual response object from the proxy ***
                        //console.log('Attentive Response Received (Phone Only):', response);

                        // *** Check the RESPONSE BODY for errors, even if HTTP status was 2xx ***
                        // Adjust checks based on the actual structure seen in the log
                        if (response && (response.status >= 400 || response.error)) {
                            console.error('Error detected within Attentive success response:', response);
                            // Manually trigger error handling logic
                            submitButton.classList.remove('is-loading');
                            let errorMessage = response.message || 'Error submitting phone. Please try again.';
                            if (response.message && response.message.includes('EMPTY_VALUE')) {
                                errorMessage = 'Invalid phone number provided. Please check the number and try again.';
                            } else if (response.status === 400) {
                                errorMessage = 'Invalid phone number format received from server. Please try again.';
                            }

                            // Show error message (similar to .catch block)
                            const wrapper = phoneField.closest('.form-input-wrapper');
                            let errorSpan = wrapper.querySelector('.form-error-message');
                            if (!errorSpan) {
                                errorSpan = document.createElement('span');
                                errorSpan.className = 'form-error-message';
                                wrapper.appendChild(errorSpan);
                            }
                            errorSpan.textContent = errorMessage;
                            errorSpan.style.color = 'red';
                            errorSpan.style.display = 'block';
                            errorSpan.style.marginTop = '5px';
                            errorSpan.setAttribute('role', 'alert');
                            setTimeout(() => { errorSpan.classList.add('show-new-error-message'); }, 10);
                            return; // Stop further processing in .then()
                        }

                        // If no error found in the response body, proceed with success
                        //console.log('Attentive submission seems genuinely successful.');
                          setTimeout(() => { submitButton.classList.remove('is-loading'); }, 500); // Shorter delay maybe?
                         // *** IMPORTANT: Only switch state on ACTUAL success ***
                         switchToSuccessState(); // Now we call it
                          // Push specific dataLayer event for phone-only?
                          window.dataLayer.push({ event: 'dl_sale_signup_phone_only' });
                          window.dataLayer.push({ event: 'dl_sale_form_signup' }); // Keep generic one?
                    })
                    .catch(jqXHR => { // jQuery AJAX passes jqXHR object on failure
                        submitButton.classList.remove('is-loading');

                        let errorMessage = 'Error submitting phone. Please try again.';
                        let errorStatus = jqXHR.status; // Get HTTP status code
                        let errorResponse = jqXHR.responseJSON || { message: jqXHR.responseText }; // Get response body

                        //console.error('Error submitting phone only to Attentive:', errorStatus, errorResponse);

                        // Customize message based on known errors
                        if (errorStatus === 400 && errorResponse && errorResponse.message && errorResponse.message.includes('EMPTY_VALUE')) {
                            errorMessage = 'Invalid phone number provided. Please check the number and try again.';
                        } else if (errorStatus === 400) {
                            errorMessage = 'Invalid phone number format. Please try again.';
                        } // Add more specific error handling as needed

                         // Show error message near the phone field
                         const wrapper = phoneField.closest('.form-input-wrapper');
                         let errorSpan = wrapper.querySelector('.form-error-message');
                         if (!errorSpan) {
                            errorSpan = document.createElement('span');
                            errorSpan.className = 'form-error-message';
                            wrapper.appendChild(errorSpan);
                         }
                         errorSpan.textContent = errorMessage; // Use the determined error message
                         errorSpan.style.color = 'red';
                         errorSpan.style.display = 'block';
                         errorSpan.style.marginTop = '5px';
                         errorSpan.setAttribute('role', 'alert');
                         setTimeout(() => { errorSpan.classList.add('show-new-error-message'); }, 10);
                    });


            // Case 2: Email field is present (Phone field may or may not be present)
            } else if (hasEmailField && isEmailValid) {
                 //console.log('Submitting email (and potentially phone) to Klaviyo...');
                 var formData = new FormData(event.target);
                 var data = Object.fromEntries(formData); // Contains email, maybe phone, maybe other fields

                 fetch('https://krw-klav.herokuapp.com/subscribe', {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json' },
                     body: JSON.stringify(data),
                 })
                 .then((response) => {
                     if (!response.ok) {
                         return response.json().then((err) => { throw err; });
                     }
                     return response.json();
                 })
                 .then((klaviyoData) => {
                     setTimeout(() => {
                         submitButton.classList.remove('is-loading');
                     }, 500); // Shorter delay?

                     if (klaviyoData.status === 'subscribed') {
                         //console.log('Klaviyo Success:', klaviyoData);
                         switchToSuccessState(); // Switch state after primary (Klaviyo) success

                         // Push dataLayer events after Klaviyo success
                         window.dataLayer.push({ event: 'dl_sale_signup' });
                         window.dataLayer.push({ event: 'dl_sale_form_signup' });

                         // Secondary: If phone is also present and valid, submit to Attentive
                         if (hasPhoneField && isPhoneValid) {
                              //console.log('Submitting phone to Attentive (secondary)...');
                              // Log details for secondary call too
                              //console.log(`[Secondary Attentive] Email: ${emailValue}, Phone Input: ${phoneValue}`);
                               const sourceId = $('input[name="attentive_id"]').val() || (window.signupPageData ? window.signupPageData.attentiveID : null);
                               submitToAttentive(emailValue, phoneValue, iti, sourceId)
                                  .then(attentiveResp => { /* Secondary Attentive call succeeded, no action needed here */ })
                                  .catch(jqXHR => { // Add detailed logging for secondary failure too
                                       let errorStatus = jqXHR.status;
                                       let errorResponse = jqXHR.responseJSON || { message: jqXHR.responseText };
                                       console.error('Error submitting to Attentive (Secondary):', errorStatus, errorResponse);
                                   });
                                 // Don't show error state for secondary failure, primary succeeded. Maybe log it.
                         }

                     } else {
                         // Klaviyo returned a non-subscribed status
                         throw new Error(klaviyoData.message || 'Subscription failed');
                     }
                 })
                 .catch((error) => {
                     submitButton.classList.remove('is-loading');
                     console.error('Error subscribing via Klaviyo:', error);
                     let errorMessage = 'Error subscribing. Please try again.';
                     if (error.message) {
                         if (error.message.includes('value is not a valid email address')) {
                             errorMessage = 'Please enter a valid email address.';
                             showErrorMessage(emailField, errorMessage, null); // Show near email field
                         } else {
                             errorMessage = error.message;
                              // Show general error near the most relevant field (email) or button
                             showErrorMessage(emailField, errorMessage, null);
                         }
                     } else if (error.detail) {
                         errorMessage = error.detail;
                          showErrorMessage(emailField, errorMessage, null);
                     } else if (error.errors && error.errors.length > 0) {
                         errorMessage = error.errors[0].msg;
                         showErrorMessage(emailField, errorMessage, null);
                     } else {
                         // Fallback generic error shown near email field
                         showErrorMessage(emailField, errorMessage, null);
                     }
                 });

             // Case 3: Neither field is present (should not happen with proper form setup)
             } else if (!hasEmailField && !hasPhoneField) {
                 //console.warn('Form submitted with no email or phone field.');
                 submitButton.classList.remove('is-loading');
                 // Optionally show a generic error message
             } else {
                  // This case handles if validation failed for the available fields,
                  // but the errors should have already been displayed.
                  //console.log('Form submission stopped due to validation errors.');
                  submitButton.classList.remove('is-loading');
             }
        });
    }
});